import React, { useContext } from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import { flowRight, get } from 'lodash'
import { withTranslationCustom } from 'lib/i18next'
import { Redirect, withRouter } from 'react-router-dom'
import { withErrorBoundary } from 'lib/errorBoundary'
import * as Nope from 'lib/utils/validation/Nope'
import * as Yup from 'yup'
import { ERROR_TYPE } from 'lib/errors'
import { GenericModalContext } from 'components/Modal/GenericModal/GenericModal'
import { getFullPath } from 'lib/location'
import { getEventModalUrl } from 'lib/utils/store/event'
import { EVENT_FORM_MODE } from 'components/Events/EventForm/EventForm'

import { Field, Form } from 'formik'
import Input from 'components/Forms/FormElements/Input'
import ExtendedFormik from 'components/Forms/ExtendedFormik'
import Button from 'components/UI/Button'
import VehicleFuelConsumptionLogic from './VehicleFuelConsumptionLogic'
import ErrorComponent from 'components/Errors/ErrorComponent'
import LoadingIndicator from 'components/LoadingIndicator'
import AppContext from 'components/AppContext'
import VehicleFuelConsumptionDisplay from './VehicleFuelConsumptionDisplay/VehicleFuelConsumptionDisplay'

import './vehicle-fuel-consumption.pcss'

const VehicleFuelConsumption = ({ t, location, eventId, mileage, vehicleId, onBackToEdit, match, history }) => {
    const modalContext = useContext(GenericModalContext)

    const renderMissingDataForm = (vehicleFuelConsumptionLogic, appError) => {
        const prevMileage = get(appError, 'data.mileage', false)

        const renderSkipButton = (
            <Button
                type="button"
                className="button button--secondary"
                trackName="skip"
                onClick={modalContext.closeModal}
            >
                {t('common:skip')}
            </Button>
        )

        const renderSubmitButton = (submitForm, isSubmitting) => (
            <Button
                type="button"
                className="button"
                disabled={isSubmitting}
                loading={isSubmitting}
                onClick={submitForm}
                trackName="submit"
            >
                {t('common:continue')}
            </Button>
        )

        return (
            <ExtendedFormik
                id="vehicle-calculate-fuel-consumption-form"
                initialValues={{
                    mileage: parseInt(mileage) || '',
                }}
                initialTouched={{
                    mileage: 'true'
                }}
                initialErrors={{
                    mileage: mileage ? t('formElements:fuelConsumption.validation.mileage') : t('formElements:validation.required')
                }}
                validationSchema={Nope.schema({
                    mileage: Yup.number()
                        .moreThan(parseInt(prevMileage), 'formElements:fuelConsumption.validation.mileage')
                        .required('formElements:validation.required'),
                })}
                onSubmit={async (values, actions) => {
                    await vehicleFuelConsumptionLogic.calculateFuelConsumption(eventId, parseInt(values.mileage), vehicleId, true)
                        .then(() => {
                            actions.setSubmitting(false)
                        })
                        .catch(() => {
                            actions.setSubmitting(false)
                        })
                }}
            >
                {({ isSubmitting, submitForm }) => (
                    <Form noValidate>
                        <h2>{t('vehicles:fuelConsumption.title')}</h2>
                        <p>{t('vehicles:fuelConsumption.missingDataForm.description')}</p>
                        <Field
                            type="number"
                            name="mileage"
                            label={t('formElements:mileage.label')}
                            placeholder={prevMileage ? `${t('vehicles:fuelConsumption.prevMileageTip')}: ${prevMileage} km` : ''}
                            descriotion=""
                            unitsTip="km"
                            size="s"
                            required
                            component={Input}
                        />
                        <div className="action-wrapper s-up-only">
                            {renderSubmitButton(submitForm, isSubmitting)}
                            {renderSkipButton}
                            {modalContext.mobileButtonsDOMElement && (
                                ReactDOM.createPortal(
                                    <>
                                        {renderSkipButton}
                                        {renderSubmitButton(submitForm, isSubmitting)}
                                    </>,
                                    modalContext.mobileButtonsDOMElement
                                )
                            )}
                        </div>
                    </Form>
                )}
            </ExtendedFormik>
        )
    }

    const renderActions = (vehicleFuelConsumptionLogic) => (
        <div className="vehicle-fuel-consumption__actions">
            <Button
                className="button"
                onClick={modalContext.closeModal}
                trackName="ok"
            >
                {t('common:ok')}
            </Button>
            <Button
                className="button button--secondary"
                trackName="edit"
                onClick={() => {
                    switch (match.params.formMode.toUpperCase()) {
                    case EVENT_FORM_MODE.CREATE_EVENT:
                        history.replace(getEventModalUrl(location, EVENT_FORM_MODE.EDIT, vehicleId, eventId))
                        break
                    case EVENT_FORM_MODE.EDIT:
                        onBackToEdit()
                        break
                    }
                }}
            >
                {t('common:backToEdit')}
            </Button>
        </div>
    )

    return (
        <AppContext className="vehicleFuelConsumption">
            <div className="vehicle-fuel-consumption">
                <VehicleFuelConsumptionLogic
                    initState={{ loading: true }}
                    onMount={(logic) => logic.calculateFuelConsumption(eventId, mileage, vehicleId, true)}
                >
                    {(vehicleFuelConsumptionLogic) => {
                        const { error, loading, data } = vehicleFuelConsumptionLogic

                        if (error) {
                            return (
                                <ErrorComponent
                                    container={false}
                                    error={error}
                                    render={(appError) => {
                                    // Try to handle errors here where we could continue with additional user input
                                        if (appError.type === ERROR_TYPE.MY_NETTIX) {
                                            if (appError.code === 10047) { // no previous fill up
                                                // close event modal
                                                return <Redirect to={getFullPath(location, { hash: false })} />
                                            } else if (appError.code === 10046) { // mileage missing
                                                return renderMissingDataForm(vehicleFuelConsumptionLogic, appError)
                                            } else if (appError.code === 10045) { // mileage too small
                                                return renderMissingDataForm(vehicleFuelConsumptionLogic, appError)
                                            }
                                        }
                                    }}
                                />
                            )
                        }

                        if (loading) return <LoadingIndicator />

                        return (
                            <>
                                <VehicleFuelConsumptionDisplay
                                    fuelConsumption={data.calculateConsumption}
                                    vehicleId={vehicleId}
                                    onBackToEdit={onBackToEdit}
                                />
                                <div className="action-wrapper s-up-only">
                                    {renderActions(vehicleFuelConsumptionLogic)}
                                    {modalContext.mobileButtonsDOMElement && (
                                        ReactDOM.createPortal(
                                            renderActions(vehicleFuelConsumptionLogic),
                                            modalContext.mobileButtonsDOMElement
                                        )
                                    )}
                                </div>
                            </>
                        )
                    }}
                </VehicleFuelConsumptionLogic>
            </div>
        </AppContext>
    )
}

VehicleFuelConsumption.propTypes = {
    eventId: PropTypes.string,
    mileage: PropTypes.number,
    vehicleId: PropTypes.string.isRequired,
    onBackToEdit: PropTypes.func.isRequired,
}

export default flowRight(
    withErrorBoundary,
    withRouter,
    withTranslationCustom(['formElements', 'vehicles']),
)(VehicleFuelConsumption)
